// @perlab/ui — Cross-tab + bridge context objects (Tier 1 V3 Slice 1).
//
// This file is the SINGLE source of truth for the context-object identities
// that wire the cross-tab bus, the IDB-backed kv store, and the iframe host
// bridge into `useTheme` (and Slice 2's `useSidebar`). Living in @perlab/ui
// keeps the import graph one-directional:
//
//   apps/shell/.../BusProvider.tsx  ─imports─►  this file (creates + provides)
//   packages/ui/.../useTheme.ts     ─imports─►  this file (consumes)
//
// Putting these contexts in @perlab/shell would require @perlab/ui to reverse-
// import from @perlab/shell — forbidden by the package layering rule. Putting
// them in @perlab/cross-tab would require @perlab/cross-tab to take a React
// peer dep just for createContext (currently zero runtime deps — see spec §11).
//
// Implementation notes:
//   - All three foreign types are imported as `type` only — TS erases them at
//     build time; no runtime dependency on @perlab/cross-tab or
//     @perlab/iframe-bridge is introduced. `@perlab/iframe-bridge` is declared
//     in @perlab/ui's devDependencies (workspace:*) purely so the type
//     resolution succeeds at build time per TL ruling — option (b) preferred
//     over a local structural interface to avoid drift when Slice 2/3 extend
//     HostBridge.
//   - All three hooks return `| null` on missing provider. Consumers are
//     EXPECTED to handle null (focus-guard fail-closed when bridge is null;
//     bus subscribe no-op when bus is null). Throwing would be wrong: useTheme
//     pre-dates the Tier 1 V3 wiring and must still work in tests/storybook
//     contexts where no BusProvider is mounted.

import { createContext, useContext } from "react";
import type { CrossTabBus, KVStore } from "@perlab/cross-tab";
import type { HostBridge } from "@perlab/iframe-bridge";

export const BusContext = createContext<CrossTabBus | null>(null);
export const KvContext = createContext<KVStore | null>(null);
export const BridgeContext = createContext<HostBridge | null>(null);

/** Read the bus from the nearest <BusContext.Provider>. Null when absent. */
export function useBus(): CrossTabBus | null {
  return useContext(BusContext);
}

/** Read the kv store from the nearest <KvContext.Provider>. Null when absent. */
export function useKv(): KVStore | null {
  return useContext(KvContext);
}

/**
 * Read the host bridge from the nearest <BridgeContext.Provider>. Null when
 * absent — focus-guard MUST fail closed (defer when focused) on null. The
 * full HostBridge surface from @perlab/iframe-bridge is available; useTheme
 * (Slice 1) only calls `queryUnsavedState`, but the typed handle is exposed
 * so Slice 2/3 consumers (sendNotification, etc.) can reuse without re-plumbing.
 */
export function useBridge(): HostBridge | null {
  return useContext(BridgeContext);
}
